import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Button, ButtonGroup, Col, Form, Row, Table } from 'react-bootstrap';
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import PageHeader from '../components/PageHeader';
import StatusMessage from '../components/StatusMessage';
import { useAsync } from '../hooks/useAsync';
import { loadHealthRange } from '../utils/healthCache';
import {
  DEFAULT_PRESET,
  DayPoint,
  MetricDef,
  METRICS,
  Period,
  RANGE_PRESETS,
  customRangeError,
  isMetricKey,
  periodFor,
  presetPeriod,
  summarize,
  toPoints,
  trendOf,
} from '../utils/healthMetrics';
import { formatDate, formatNumber, isoDaysAgo, shortDate } from '../utils/format';
import { Y_AXIS, yAxisProps } from '../utils/axis';

/** Horizontal pixels per day when the range does not fit the screen. The chart scrolls sideways past that. */
const PX_PER_DAY = 8;
const PAGE_SIZE = 25;
const AXIS = { stroke: 'var(--text-muted)', fontSize: 12 };
const MARGIN = { top: 8, right: 8, bottom: 0, left: -12 };

/** Reads the period from the URL: ?from&to for a custom range, otherwise ?range=<preset>. */
function periodFromParams(params: URLSearchParams): Period {
  const today = isoDaysAgo(0);
  const from = params.get('from');
  const to = params.get('to');
  if (from && to && customRangeError(from, to, today) === null) return periodFor(from, to, 'custom');
  const key = params.get('range') ?? DEFAULT_PRESET;
  return presetPeriod(RANGE_PRESETS.some(p => p.key === key) ? key : DEFAULT_PRESET);
}

const formatValue = (value: number | null | undefined, digits = 0, unit?: string) =>
  value == null ? '—' : `${formatNumber(value, digits)}${unit ? ` ${unit}` : ''}`;

/** Route page for /health/:metric. */
const MetricHistoryPage: React.FC = () => {
  const { metric: key = '' } = useParams();
  if (!isMetricKey(key)) {
    return (
      <>
        <PageHeader title="Unknown metric">
          <p>
            There is no history page for &ldquo;{key}&rdquo;. <Link to="/health">Back to Health</Link>
          </p>
        </PageHeader>
      </>
    );
  }
  return <MetricHistory metric={METRICS[key]} />;
};

const MetricHistory: React.FC<{ metric: MetricDef }> = ({ metric }) => {
  const [params, setParams] = useSearchParams();
  const period = useMemo(() => periodFromParams(params), [params]);
  const primary = metric.fields[0];
  const digits = primary.digits ?? 0;
  const unit = primary.unit;

  const [customOpen, setCustomOpen] = useState(period.preset === 'custom');
  const [draftFrom, setDraftFrom] = useState(period.from);
  const [draftTo, setDraftTo] = useState(period.to);
  const [customError, setCustomError] = useState<string | null>(null);

  const history = useAsync(
    async () => {
      const [rows, previousRows] = await Promise.all([
        loadHealthRange(metric.resource, { from: period.from, to: period.to }),
        loadHealthRange(metric.resource, period.previous),
      ]);
      return { points: toPoints(metric, rows), previous: toPoints(metric, previousRows) };
    },
    [metric.key, period.from, period.to]
  );

  // While a new range loads, the previous range's numbers are not shown.
  const shown = history.loading ? null : history.data;
  const stats = shown ? summarize(shown.points, primary.key, digits) : null;
  const previousStats = shown ? summarize(shown.previous, primary.key, digits) : null;
  const trend = stats && previousStats ? trendOf(stats.average, previousStats.average, digits) : null;

  const openCustom = () => {
    setDraftFrom(period.from);
    setDraftTo(period.to);
    setCustomError(null);
    setCustomOpen(true);
  };

  const applyCustom = (event: React.FormEvent) => {
    event.preventDefault();
    const error = customRangeError(draftFrom, draftTo, isoDaysAgo(0));
    setCustomError(error);
    if (!error) setParams({ from: draftFrom, to: draftTo });
  };

  return (
    <>
      <Link to="/health" className="small d-inline-block mb-1">
        <span aria-hidden="true">‹</span> Health
      </Link>
      <PageHeader title={metric.title}>
        <p>{metric.description}</p>
      </PageHeader>

      <div className="range-bar mb-2" role="group" aria-label="Date range">
        <ButtonGroup>
          {RANGE_PRESETS.map(p => {
            const active = period.preset === p.key;
            return (
              <Button
                key={p.key}
                variant={active ? 'primary' : 'outline-secondary'}
                aria-pressed={active}
                onClick={() => {
                  setCustomOpen(false);
                  setParams({ range: p.key });
                }}
              >
                {p.label}
              </Button>
            );
          })}
          <Button variant={customOpen ? 'primary' : 'outline-secondary'} aria-pressed={customOpen} onClick={openCustom}>
            Custom
          </Button>
        </ButtonGroup>
      </div>

      {customOpen && (
        <Form onSubmit={applyCustom} className="range-custom surface mb-3" aria-label="Custom date range">
          <div className="d-flex flex-wrap align-items-end gap-2">
            <Form.Group controlId="history-from">
              <Form.Label className="small muted mb-1">From</Form.Label>
              <Form.Control type="date" value={draftFrom} max={draftTo || undefined} onChange={e => setDraftFrom(e.target.value)} />
            </Form.Group>
            <Form.Group controlId="history-to">
              <Form.Label className="small muted mb-1">To</Form.Label>
              <Form.Control type="date" value={draftTo} min={draftFrom || undefined} onChange={e => setDraftTo(e.target.value)} />
            </Form.Group>
            <Button type="submit" variant="primary">
              Show range
            </Button>
          </div>
          {customError && (
            <p role="alert" className="text-danger small mt-2 mb-0">
              {customError}
            </p>
          )}
        </Form>
      )}

      <p className="muted small mb-3">
        {formatDate(period.from)} to {formatDate(period.to)} ({period.days} days)
      </p>

      <StatusMessage loading={history.loading} error={history.error} onRetry={history.reload} />

      {shown && stats && previousStats && (
        <>
          <Row className="g-3 mb-3">
            <Col xs={6} md={3}>
              <section className="surface">
                <div className="stat-label">Latest</div>
                <div className="stat-value tabular">{formatValue(stats.latest?.value, digits, unit)}</div>
                <div className="stat-hint">{stats.latest ? `On ${formatDate(stats.latest.date)}` : 'No value in this period'}</div>
              </section>
            </Col>
            <Col xs={6} md={3}>
              <section className="surface">
                <div className="stat-label">Average</div>
                <div className="stat-value tabular">{formatValue(stats.average, digits, unit)}</div>
                <div className="stat-hint">Over {stats.withValue} days with a value</div>
              </section>
            </Col>
            <Col xs={6} md={3}>
              <section className="surface">
                <div className="stat-label">Min and max</div>
                <div className="stat-value tabular">
                  {stats.min == null ? '—' : `${formatNumber(stats.min, digits)} – ${formatNumber(stats.max, digits)}`}
                </div>
                <div className="stat-hint">{unit ?? 'Lowest and highest day'}</div>
              </section>
            </Col>
            <Col xs={6} md={3}>
              <section className="surface">
                <div className="stat-label">Trend</div>
                <div className="stat-value tabular">{trend ? `${trend.delta > 0 ? '+' : ''}${formatNumber(trend.delta, digits)}` : '—'}</div>
                <div className="stat-hint">
                  {trend
                    ? `${trend.percent == null ? '' : `${trend.percent > 0 ? '+' : ''}${trend.percent}% `}vs the previous ${period.days} days`
                    : 'Not enough data in the previous period'}
                </div>
              </section>
            </Col>
          </Row>

          <section className="surface mb-3">
            <h2>{metric.title} by day</h2>
            <p className="muted small mb-3">
              Fetched {stats.fetched} of {period.days} days, {stats.withValue} with a value.
            </p>
            {stats.withValue === 0 ? (
              <NoData title={metric.title} />
            ) : (
              <HistoryChart metric={metric} points={shown.points} period={period} />
            )}
          </section>

          {stats.fetched > 0 && (
            <section className="surface">
              <h2>Daily values</h2>
              <DayTable key={`${period.from}|${period.to}`} metric={metric} points={shown.points} />
            </section>
          )}
        </>
      )}
    </>
  );
};

const NoData: React.FC<{ title: string }> = ({ title }) => (
  <div className="history-empty">
    <p className="fw-semibold mb-1">No data in this period</p>
    <p className="muted mb-0">
      Garmin may not have returned {title.toLowerCase()} for these days, so there is nothing to chart. Try a longer range, or
      check the sync status in Settings.
    </p>
  </div>
);

/** The axis rule of a metric: a fixed range such as 0-100, a baseline of zero, or just the data range. */
function metricAxis(metric: MetricDef): { fixed?: [number, number]; zero?: boolean } {
  const [lo, hi] = metric.domain ?? [];
  if (typeof lo === 'number' && typeof hi === 'number') return { fixed: [lo, hi] };
  return { zero: lo === 0 };
}

/** Daily values. Long ranges scroll sideways (touch or trackpad); the view starts at the newest day. */
const HistoryChart: React.FC<{ metric: MetricDef; points: DayPoint[]; period: Period }> = ({ metric, points, period }) => {
  const scroller = useRef<HTMLDivElement>(null);
  const data = points.map(p => ({ date: shortDate(p.date), ...p.values }));
  const plotted = metric.fields.filter(f => f.plot);

  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [period.from, period.to]);

  const axes = [
    <CartesianGrid key="grid" stroke="var(--chart-grid)" vertical={false} />,
    <XAxis key="x" dataKey="date" {...AXIS} minTickGap={16} />,
    <YAxis key="y" {...Y_AXIS} {...yAxisProps(data, plotted.map(f => f.key), metricAxis(metric))} />,
    <Tooltip key="tip" />,
    plotted.length > 1 ? <Legend key="legend" /> : null,
  ];

  return (
    <div className="history-scroll" ref={scroller} role="region" aria-label={`${metric.title} chart`} tabIndex={0}>
      <div className="history-chart" style={{ width: `max(100%, ${period.days * PX_PER_DAY}px)` }}>
        {/* The chart fills the scrolling width; the height is fixed so it reads the same on phones. */}
        <ResponsiveContainer width="100%" height={260}>
          {metric.chart === 'bar' ? (
            <BarChart data={data} margin={MARGIN}>
              {axes}
              {plotted.map(f => (
                <Bar key={f.key} dataKey={f.key} name={f.label} fill={f.plot!.color} radius={[3, 3, 0, 0]} />
              ))}
            </BarChart>
          ) : (
            <LineChart data={data} margin={MARGIN}>
              {axes}
              {plotted.map(f => (
                <Line
                  key={f.key}
                  type="monotone"
                  dataKey={f.key}
                  name={f.label}
                  stroke={f.plot!.color}
                  strokeWidth={2}
                  strokeDasharray={f.plot!.dashed ? '4 4' : undefined}
                  dot={false}
                  connectNulls={false}
                />
              ))}
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const DayTable: React.FC<{ metric: MetricDef; points: DayPoint[] }> = ({ metric, points }) => {
  const [page, setPage] = useState(0);
  const newestFirst = useMemo(() => [...points].reverse(), [points]);
  const pages = Math.max(1, Math.ceil(newestFirst.length / PAGE_SIZE));
  const shown = newestFirst.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <>
      <div className="table-responsive">
        <Table size="sm" className="trainer-table">
          <thead>
            <tr>
              <th>Date</th>
              {metric.fields.map(f => (
                <th key={f.key} className="text-end">
                  {f.unit ? `${f.label} (${f.unit})` : f.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map(p => (
              <tr key={p.date}>
                <td className="tabular">{formatDate(p.date)}</td>
                {metric.fields.map(f => (
                  <td key={f.key} className="text-end tabular">
                    {formatNumber(p.values[f.key], f.digits ?? 0)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <nav aria-label="Daily values pages" className="d-flex justify-content-between align-items-center gap-2 mt-3">
        <Button variant="outline-secondary" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} aria-label="Newer days">
          ‹ Newer
        </Button>
        <span className="muted small">
          Page {page + 1} of {pages}
        </span>
        <Button
          variant="outline-secondary"
          size="sm"
          disabled={page >= pages - 1}
          onClick={() => setPage(p => p + 1)}
          aria-label="Older days"
        >
          Older ›
        </Button>
      </nav>
    </>
  );
};

export default MetricHistoryPage;
